import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import SingleSelection from "../../../stories/core-components/SingleSelection/SingleSelectionUI";
import MultiSelection from "../../../stories/core-components/MultiSelection/MultiSelectionUI";
import {
  filterSelector,
  seoFilterSelector,
  selectedFilterSelector,
  vehicledataSelector,
  seoVehicleDataSelector,
  benefitsDataSelector,
  errorSelector,
  dispatchSelector
} from "../../pages/SearchPage/redux/selector";
import PriceFiter from "@stencil/pricefilter";
import * as S from "./Filter.styles";
import { fetchResults } from "../../pages/SearchPage/redux/thunks";
import FiltersLoaderUI from "../../../stories/components/cars/ResultsPage/Filters/FiltersLoaderUI";
import Benefits from "../../../stories/components/cars/ResultsPage/Benefits/BenefitsUI";
import Icon from "../../../stories/core-components/IconsSelector";
import { scrollToTop } from "../../config/utils";
import useNavigate from '../../hooks/useNavigate';
import { seoBenefitsDataSelector } from '../../pages/SeoPage/redux/selector'
import { CTA_TYPE, getActionNameForOmniture, pushOmniEvents } from "../../utils/omni-utils";
import { gcTripCategoryScreen, getCtaProperty, getLoadedComponent, pushCtaOmniEvents } from "../../config/omni";
import { BACK_TO_TOP, CABS_LISTING_ROUTE, FILTER_COMPONENT } from "../../utils/util-constants";
import { atModalDetailsSelector, homeHourlyRentalPackageSelector, osModalDetailsSelector } from "../../common/redux/selector";

export interface IFilter {
  isSeo?: boolean
}

const Filter: React.FC<IFilter> = ({ isSeo = false }) => {
  const dispatch = useDispatch();
  const filter = isSeo ? useSelector(seoFilterSelector) : useSelector(filterSelector);
  const selectedFilter = useSelector(selectedFilterSelector);
  const { sf = [], msf = [], pf = {} } = selectedFilter;
  const vehicledata = isSeo ? useSelector(seoVehicleDataSelector) : useSelector(vehicledataSelector);
  const error = useSelector(errorSelector);
  const { m_s_f = [], s_f = [], p_f = {} } = filter;
  const { s_min_a: min = 0, s_max_a: max = 10000, max_a = "", min_a = "" } = p_f;
  const benefitsData = useSelector(isSeo ? seoBenefitsDataSelector : benefitsDataSelector);
  const [navigateTo] = useNavigate();
  const dispatchSearchId = useSelector(dispatchSelector);
  const hrDetails = useSelector(homeHourlyRentalPackageSelector)
  const osModalDetails = useSelector(osModalDetailsSelector)
  const atModalDetails = useSelector(atModalDetailsSelector)

  useEffect(() => {
    getLoadedComponent(FILTER_COMPONENT)
  }, [])
  
  // filter_enabled check is removed for dweb and will add back once it is enabled
  if (Object.keys(error)?.length && !Object.keys(selectedFilter)?.length) {
    return <></>;
  }
  if (!m_s_f?.length && !s_f?.length && !p_f?.length) {
    return <FiltersLoaderUI />;
  }

  const sendOmniEventOnFilter = (appliedFilter : string, appliedFiltersList : string, isFilterApplied : boolean, oc:string, fc:string) => {
    const filterName = oc ? fc === "PAX" ? `${oc}_${fc}` : oc : "price"
    const ctaName   : string = isFilterApplied ? `${filterName}_filter_apply` : `${filterName}_filter_clear`
    const eventDetails = {
      cta: getCtaProperty(getActionNameForOmniture(gcTripCategoryScreen, ctaName), CTA_TYPE.BUTTON, FILTER_COMPONENT, CABS_LISTING_ROUTE),
      filter: {
        appliedFiltersList: appliedFiltersList
      }
    }

    eventDetails.filter[isFilterApplied ? "filterSelected" : "filterRemoved"] = appliedFilter
    pushOmniEvents({eventName: isFilterApplied ? "filterSelection" : "filterRemoval", details: eventDetails})
  }

  const getMsfFilterString = (msf: Array<{fc: string, opt_code: string[]}>) : string => {
    let appliedFiltersList = ""
    if (msf.length) {
      msf.forEach((filter : {fc : string, opt_code : string[]}) => {
        const optCode = filter?.opt_code || []
        if (optCode.length > 1) {
          optCode.forEach(code => {
            appliedFiltersList += appliedFiltersList.length ? ` | ${filter?.fc} : ${code}` : `${filter?.fc} : ${code}`
          })
        } else {
          appliedFiltersList += appliedFiltersList.length ? ` | ${filter?.fc} : ${optCode[0]}` : `${filter?.fc} : ${optCode[0]}`
        }
      })
    }

    return appliedFiltersList
  }

  const updateFilter = ({ type, oc, is_applied, fc, min, max }) => {
    let newFilter = JSON.parse(JSON.stringify(selectedFilter));
    switch (type) {
      case "sf": {
        let isFound = 0;
        if (newFilter?.sf?.length) {
          newFilter?.sf.forEach((data, index) => {
            if (data.fc === fc) {
              isFound++;
              newFilter.sf.splice(index, 1);
            }
          });
          if (!isFound) {
            newFilter.sf.push({ fc });
          }
        } else {
          newFilter.sf = [{ fc }];
        }
        break;
      }
      case "msf": {
        let isFound = 0;
        if (newFilter?.msf?.length) {
          newFilter?.msf.forEach((data, index) => {
            if (data["fc"] === fc) {
              isFound++;
              if (is_applied) {
                newFilter.msf[index].opt_code.push(oc);
              } else {
                if (newFilter.msf[index].opt_code.indexOf(oc) > -1) {
                  if (newFilter.msf[index].opt_code.length === 1) {
                    newFilter.msf.splice(index, 1);
                  } else {
                    newFilter.msf[index].opt_code.splice(
                      newFilter.msf[index].opt_code.indexOf(oc),
                      1
                    );
                  }
                }
              }
            }
          });
          if (!isFound) {
            newFilter.msf.push({ fc, opt_code: [oc] });
          }
        } else {
          newFilter.msf = [{ fc, opt_code: [oc] }];
        }
        break;
      }
      default: {
        newFilter["pf"] = { s_min_a: min, s_max_a: max };
        break;
      }
    }

    let appliedFilter       : string  = ""
    let appliedFiltersList  : string  = ""

    if (fc && oc) {
      // adding applied filter for msf
      appliedFilter = `${fc} : ${oc}`
    }

    appliedFiltersList = getMsfFilterString(newFilter?.msf || [])

    if (min || max) {
      // adding applied filter for price filter
      appliedFilter = `min_a : ${min} | max_a : ${max}`
      appliedFiltersList += appliedFiltersList.length ? ` | ${appliedFilter}` : `${appliedFilter}`
    }

    // appliedFilter is current filter and appliedFilterList is total filters
    sendOmniEventOnFilter(appliedFilter, appliedFiltersList, min || max ? true : is_applied, oc, fc)
    dispatch({
      type: "UPDATE_FILTERS",
      filters: newFilter,
    });
    if (isSeo) {
      navigateTo('srp', true)
    } else {
      dispatch(fetchResults({dispatchSearchId}));
    }
  };

  const clearFilter = () => {
    if (Object.keys(selectedFilter)?.length) {
      //creating a list of all the removed msf filters to pass in event
      let removedFilterList = getMsfFilterString(selectedFilter?.msf || [])
      if (selectedFilter?.pf) {
        //adding price filter in the removed filter list
        removedFilterList += removedFilterList.length ? ` | min_a : ${selectedFilter?.pf?.s_min_a} | max_a : ${selectedFilter?.pf?.s_max_a}` : `min_a : ${selectedFilter?.pf?.s_min_a} | max_a : ${selectedFilter?.pf?.s_max_a}`
      }
      sendOmniEventOnFilter(removedFilterList, "", false, "", "")
      dispatch({
        type: "CLEAR_FILTER",
        filters: {},
      });
      dispatch(fetchResults({dispatchSearchId}));
    }
    if (isSeo) {
      navigateTo('srp', true)
    }
  };

  const onReset = ({ fc, pf = false }) => {
    let removedFiltersList  : string = ""
    let appliedFilters      : string = ""
    const newFilter = JSON.parse(JSON.stringify(selectedFilter));
    const { msf: newMsf = [] } = newFilter;
    let isIndex: any = "";
    newMsf.forEach((data, index) => {
      if (data.fc === fc) {
        isIndex = index;
        return;
      }
    });
    const resetFilters = [{...newMsf[isIndex]}]
    newMsf.splice(isIndex, 1);
    if (pf) {
      //creating a list of all the removed filters and adding if price filter is removed
      removedFiltersList = `min_a : ${newFilter?.pf?.s_min_a} | max_a : ${newFilter?.pf?.s_max_a}`
      delete newFilter.pf;
    }

    //list of all the removed msf filters to pass in event
    removedFiltersList += removedFiltersList.length ? ` | ${getMsfFilterString(resetFilters)}` : getMsfFilterString(resetFilters)
    appliedFilters = getMsfFilterString(newFilter?.msf || [])

    if (newFilter?.pf) {
      appliedFilters += appliedFilters.length ? ` | min_a : ${newFilter?.pf?.s_min_a} | max_a : ${newFilter?.pf?.s_max_a}` : `min_a : ${newFilter?.pf?.s_min_a} | max_a : ${newFilter?.pf?.s_max_a}`
    }
    
    sendOmniEventOnFilter(removedFiltersList, appliedFilters, false, "", "")
    dispatch({
      type: "UPDATE_FILTERS",
      filters: newFilter,
    });
    if (isSeo) {
      navigateTo('srp', true)
    } else {
      dispatch(fetchResults({dispatchSearchId}));
    }
  };

  return (
    <>
      {(vehicledata?.length || (atModalDetails?.airportModalData || osModalDetails?.osModalData || hrDetails?.rentalModalDetails)) ? (
        <Benefits {...benefitsData} />
      ) : null}
      <S.FiltersCont>
        <div className="filtersHeading">
          <p className="makeFlex spaceBetween hrtlCenter">
            <span className="filtersText">Filters</span>
            <span className="clearText" style={Object.keys(selectedFilter)?.length ? {color: '#2276e3' } : {}} onClick={clearFilter}>
              Clear
            </span>
          </p>
          <p className="filtersSubHeading">{`Showing ${vehicledata?.length} Car variation`}</p>
        </div>
        <ul className="segmentWrap">
          {s_f?.map((item, index) => {
            const isActive = sf?.some(({ fc }) => fc === item.fc);
            return (
              <SingleSelection
                {...item}
                onToggle={updateFilter}
                isActive={isActive}
              />
            );
          })}
        </ul>
        {Object.keys(p_f)?.length ? (
          <S.FilterRows>
            <ul>
              <S.FilterRow index={`priceFilter`}>
                <h4>
                  <span className="makeFlex hrtlCenter">
                    {/* <Arrow
                  variation={'down'}
                  arrowWidth={"0.5rem"}
                  margin={"0 0.8rem 0 0"}
                  borderColor={"#000"}
                /> */}
                    {"Price Filter"}
                  </span>
                  <span
                    className="resetText"
                    onClick={() => onReset({ pf: true })}
                  >
                    RESET
                  </span>
                </h4>
                <ul>
                  <PriceFiter
                    onChangeComplete={updateFilter}
                    min={min_a}
                    max={max_a}
                  />
                </ul>
              </S.FilterRow>
            </ul>
          </S.FilterRows>
        ) : null}
        <S.FilterRows>
          <ul>
            {m_s_f?.map(({ t = "", options = [], fc = "" }, index) => (
              <S.FilterRow index={`FilterRows${index}`}>
                <h4>
                  <span className="makeFlex hrtlCenter">
                    {/* <Arrow
                  variation={'down'}
                  arrowWidth={"0.5rem"}
                  margin={"0 0.8rem 0 0"}
                  borderColor={"#000"}
                /> */}
                    {t}
                  </span>
                  <span className="resetText" style={msf?.length ? {color: '#2276e3' } : {}} onClick={() => onReset({ fc })}>
                    RESET
                  </span>
                </h4>
                <ul>
                  {options.map((item, index) => {
                    const isActive = msf?.some(
                      ({ opt_code, fc: filterCode }) =>
                        filterCode === fc && opt_code?.indexOf(item?.oc) > -1
                    );
                    return (
                      <li className="" key={`MultiSelection${index}`}>
                        <MultiSelection
                          {...item}
                          onToggle={updateFilter}
                          fc={fc}
                          checked={isActive}
                          isSeo={isSeo}
                        />
                      </li>
                    );
                  }) || null}
                </ul>
              </S.FilterRow>
            ))}
          </ul>
        </S.FilterRows>
      </S.FiltersCont>
      <div className="appendTop16">
        <S.BackToTopBtn onClick={() => {
          pushCtaOmniEvents(getActionNameForOmniture(gcTripCategoryScreen, BACK_TO_TOP), CTA_TYPE.BUTTON, FILTER_COMPONENT, CABS_LISTING_ROUTE)
          scrollToTop()
        }}>
          Back to Top
          <S.Shape>
            <Icon
              name="ArrowTailUpIcon"
              styleProps={{
                width: "1.6rem",
                height: "1.6rem",
                fill: "#fff",
              }}
            />
          </S.Shape>
        </S.BackToTopBtn>
      </div>
    </>
  );
};

export default React.memo(Filter);
